import React, { useRef, useState, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Flame, MapPin, Printer, Download, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";

const PRODUCTION_URL = "https://smokey-af-bbq.emergent.host";

// All 24 items, current prices — split 12 front / 12 back
const FRONT_ITEMS = [
  { img: "/menu/bbq_pizza.png", name: "Smokey AF Pizza", price: "$16" },
  { img: "/menu/nacho_pork_pizza.png", name: "Nacho Pork Pizza", price: "$16" },
  { img: "/menu/pepperoni_pizza.png", name: "Pepperoni Pizza", price: "$16" },
  { img: "/menu/chicken_alfredo_pizza.png", name: "Chicken Alfredo Pizza", price: "$16" },
  { img: "/menu/chicken_bacon_ranch_pizza.png", name: "Chicken Bacon Ranch", price: "$16" },
  { img: "/menu/betty_cracker_chicken_bowl.png", name: "Betty Cracker Chicken Bowl", price: "$14" },
  { img: "/menu/pork_crack_mash_bowl.png", name: "Pork Crack Mash Bowl", price: "$14" },
  { img: "/menu/bbq_boat_hero.png", name: "BBQ Boat", price: "$14" },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/6ad5284b-01f5-432e-9dd7-8388ae8bdde3/images/ea2515a2333c003b27a647814b302029d3cce5e873cb14aabd10c36f28866c54.png",
    name: "Pulled Pork Sandwich Meal",
    price: "$14",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/6ad5284b-01f5-432e-9dd7-8388ae8bdde3/images/7bf2ff7d38aa446f0d911781f9ff37801a21fc0c06fdec41228765bfa8ea3d62.png",
    name: "Japanese BBQ Burger",
    price: "$14",
  },
  { img: "/menu/loaded_pork_fries.png", name: "Loaded Pork Fries", price: "$14" },
  { img: "/menu/bbq_bavarian_pretzel.png", name: "Bavarian Behemoth Pretzel", price: "$12" },
];
const BACK_ITEMS = [
  { img: "/menu/garlic_parmesan_wings.png", name: "Garlic Parm Wings", price: "$12" },
  { img: "/menu/bbq_wings.png", name: "BBQ Wings", price: "$12" },
  { img: "/menu/buffalo_wings.png", name: "Buffalo Wings", price: "$12" },
  { img: "/menu/chipotle_firecracker_wings.png", name: "Chipotle Firecracker Wings", price: "$12" },
  { img: "/menu/bbq_chicken_sandwich.png", name: "BBQ Chicken Sandwich", price: "$10" },
  { img: "/menu/nacho_pork_sandwich.png", name: "Nacho Pork Sandwich", price: "$10" },
  { img: "/menu/japanese_pork_sandwich.png", name: "Japanese Pork Sandwich", price: "$10" },
  { img: "/menu/bbq_sushi.png", name: "BBQ Sushi", price: "$10" },
  { img: "/menu/jerk_chicken_sushi.png", name: "Jerk Chicken Sushi Roll", price: "$10" },
  { img: "/menu/mini_pulled_pork_sandwich.png", name: "Mini Pulled Pork", price: "$7" },
  { img: "/menu/mini_bbq_boat.png", name: "Mini BBQ Boat", price: "$7" },
  { img: "/menu/order_of_fries.png", name: "Order of Fries", price: "$5" },
];

// 8.5×11 at 96 dpi = 816 × 1056 px. Render on screen, html-to-image exports at 2× = 1632×2112 (print-ready)
const SHEET_W = 816;
const SHEET_H = 1056;
const EXPORT_SCALE = 2;

// Shared visual elements
function PageBackground() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none opacity-25 mix-blend-screen"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      }}
    />
  );
}

function FoodTile({ item }) {
  return (
    <div className="bg-[#111] border border-white/10 overflow-hidden flex flex-col">
      <div
        className="bg-cover bg-center"
        style={{
          backgroundImage: `url(${item.img})`,
          aspectRatio: "1 / 1",
          filter: "saturate(1.1) contrast(1.05)",
        }}
      />
      <div className="px-2.5 py-2.5 flex items-center justify-between gap-1.5 flex-1">
        <div
          className="font-['Anton'] uppercase text-white tracking-wider leading-tight"
          style={{ fontSize: 15 }}
        >
          {item.name}
        </div>
        <div
          className="font-['Anton'] text-[#FF8C00] flex-shrink-0"
          style={{ fontSize: 26 }}
        >
          {item.price}
        </div>
      </div>
    </div>
  );
}

function QRTile({ label }) {
  return (
    <div
      className="bg-[#FF3B30] border border-white/10 flex flex-col items-center justify-center"
      style={{ padding: 12 }}
    >
      <div className="bg-white" style={{ padding: 6 }}>
        <QRCodeSVG
          value={PRODUCTION_URL}
          size={118}
          level="H"
          bgColor="#ffffff"
          fgColor="#000000"
          includeMargin={false}
        />
      </div>
      <div
        className="font-['Anton'] uppercase text-white tracking-[0.2em] mt-2 leading-tight text-center"
        style={{ fontSize: 12 }}
      >
        {label || (
          <>
            Scan
            <br />
            to Order
          </>
        )}
      </div>
    </div>
  );
}

function QRBanner({ flipNote }) {
  return (
    <div className="relative px-8" style={{ marginTop: 10 }}>
      <div
        className="bg-[#FF3B30] flex items-center justify-between shadow-[0_8px_24px_rgba(255,59,48,0.4)]"
        style={{ padding: "10px 18px" }}
      >
        <div>
          <div
            className="font-['Anton'] uppercase text-white tracking-widest"
            style={{ fontSize: 24 }}
          >
            Scan to Order from Your Phone
          </div>
          <div
            className="font-['Outfit'] uppercase text-white/85 tracking-[0.25em] mt-1"
            style={{ fontSize: 12 }}
          >
            {flipNote}
          </div>
          <div
            className="font-['Outfit'] uppercase text-white/70 tracking-[0.2em] mt-1"
            style={{ fontSize: 11 }}
          >
            Inside Southern Style Bar · 648 N Broad St · Fremont, NE
          </div>
        </div>
        <div className="bg-white flex-shrink-0" style={{ padding: 5 }}>
          <QRCodeSVG
            value={PRODUCTION_URL}
            size={78}
            level="H"
            bgColor="#ffffff"
            fgColor="#000000"
            includeMargin={false}
          />
        </div>
      </div>
    </div>
  );
}

function BrandTile({ children, accent }) {
  return (
    <div
      className={`border border-white/10 flex flex-col items-center justify-center text-center ${
        accent ? "bg-zinc-950" : "bg-[#111]"
      }`}
      style={{ padding: 12 }}
    >
      {children}
    </div>
  );
}

export function FrontPage() {
  return (
    <div
      className="relative bg-black overflow-hidden"
      style={{
        width: SHEET_W,
        height: SHEET_H,
        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,59,48,0.22), transparent 60%), radial-gradient(ellipse 70% 50% at 50% 100%, rgba(255,140,0,0.16), transparent 60%)",
      }}
    >
      <PageBackground />

      {/* HEADER */}
      <div className="relative px-8 pt-7 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Flame
              className="text-[#FF3B30] drop-shadow-[0_0_10px_rgba(255,59,48,0.6)]"
              style={{ width: 22, height: 22 }}
            />
            <span
              className="font-['Anton'] uppercase text-white tracking-[0.3em]"
              style={{ fontSize: 17 }}
            >
              Smokey <span className="text-[#FF3B30]">AF</span> Barbecue
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-[#FF8C00]" />
            <span
              className="font-['Outfit'] uppercase text-[#FF8C00] tracking-[0.35em]"
              style={{ fontSize: 11 }}
            >
              Fremont, NE
            </span>
          </div>
        </div>

        <div className="text-center mt-2">
          <div
            className="inline-block bg-[#FF3B30] text-white font-['Anton'] uppercase tracking-[0.3em] rotate-[-1deg] shadow-[0_8px_24px_rgba(255,59,48,0.45)]"
            style={{ fontSize: 12, padding: "4px 12px" }}
          >
            Yes, We Serve Food.
          </div>
          <h1
            className="font-['Anton'] uppercase text-white leading-[0.9] mt-2"
            style={{ fontSize: 56, letterSpacing: "-0.02em" }}
          >
            Hungry? <span style={{ color: "#FF3B30" }}>Order Up.</span>
          </h1>
          <p
            className="mt-1 font-['Outfit'] italic text-zinc-300"
            style={{ fontSize: 13 }}
          >
            Slow-smoked · Hand-pulled · Wood-fired · Right here at the bar.
          </p>
        </div>
      </div>

      {/* MENU GRID — 4 cols × 3 rows = 12 food tiles */}
      <div className="relative px-8 mt-3">
        <div className="grid grid-cols-4" style={{ gap: 8 }}>
          {FRONT_ITEMS.map((item) => (
            <FoodTile key={item.name} item={item} />
          ))}
        </div>
      </div>

      <QRBanner flipNote="smokey-af-bbq.emergent.host · Flip for wings, sushi & minis" />
    </div>
  );
}

export function BackPage() {
  return (
    <div
      className="relative bg-black overflow-hidden"
      style={{
        width: SHEET_W,
        height: SHEET_H,
        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,140,0,0.18), transparent 60%), radial-gradient(ellipse 70% 50% at 50% 100%, rgba(255,59,48,0.18), transparent 60%)",
      }}
    >
      <PageBackground />

      {/* HEADER */}
      <div className="relative px-8 pt-7 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Flame
              className="text-[#FF3B30] drop-shadow-[0_0_10px_rgba(255,59,48,0.6)]"
              style={{ width: 22, height: 22 }}
            />
            <span
              className="font-['Anton'] uppercase text-white tracking-[0.3em]"
              style={{ fontSize: 17 }}
            >
              Smokey <span className="text-[#FF3B30]">AF</span> · The Rest
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4 text-[#FF8C00]" />
            <span
              className="font-['Outfit'] uppercase text-[#FF8C00] tracking-[0.35em]"
              style={{ fontSize: 11 }}
            >
              Fremont, NE
            </span>
          </div>
        </div>

        <div className="text-center mt-2">
          <div
            className="inline-block bg-[#FF8C00] text-black font-['Anton'] uppercase tracking-[0.3em] rotate-[1deg] shadow-[0_8px_24px_rgba(255,140,0,0.45)]"
            style={{ fontSize: 12, padding: "4px 12px" }}
          >
            Wings · Sushi · Minis · Sides
          </div>
          <h1
            className="font-['Anton'] uppercase text-white leading-[0.9] mt-2"
            style={{ fontSize: 56, letterSpacing: "-0.02em" }}
          >
            Pick Your <span style={{ color: "#FF3B30" }}>Side.</span>
          </h1>
          <p
            className="mt-1 font-['Outfit'] italic text-zinc-300"
            style={{ fontSize: 13 }}
          >
            Smaller plates · Bigger flavors · Same wood-fire.
          </p>
        </div>
      </div>

      {/* MENU GRID — 4 cols × 3 rows = 12 food tiles */}
      <div className="relative px-8 mt-3">
        <div className="grid grid-cols-4" style={{ gap: 8 }}>
          {BACK_ITEMS.map((item) => (
            <FoodTile key={item.name} item={item} />
          ))}
        </div>
      </div>

      <QRBanner flipNote="smokey-af-bbq.emergent.host · Flip for pizzas, bowls & mains" />
    </div>
  );
}

export default function TableFlyer() {
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [downloading, setDownloading] = useState(null); // "front" | "back" | null

  const download = useCallback(async (which) => {
    const node = which === "front" ? frontRef.current : backRef.current;
    if (!node) return;
    try {
      setDownloading(which);
      // Wait a beat to allow any deferred image decoding
      await new Promise((r) => setTimeout(r, 100));
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: EXPORT_SCALE,
        backgroundColor: "#000000",
      });
      const link = document.createElement("a");
      link.download =
        which === "front"
          ? "smokey-af-table-flyer-front.png"
          : "smokey-af-table-flyer-back.png";
      link.href = dataUrl;
      link.click();
    } catch (e) {
      alert("Couldn't generate the image. Please try again.");
      console.error(e);
    } finally {
      setDownloading(null);
    }
  }, []);

  return (
    <div className="min-h-screen bg-zinc-800 tableflyer-root py-12 px-4">
      {/* Page label */}
      <p
        data-testid="tableflyer-label"
        className="font-['Outfit'] text-zinc-300 text-sm uppercase tracking-[0.3em] text-center mb-8"
      >
        Table Flyer · Front + Back · Letter (8.5″ × 11″)
      </p>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-10">
        {/* FRONT */}
        <div className="flex flex-col items-center gap-4">
          <div
            className="font-['Anton'] uppercase tracking-[0.4em] text-zinc-400"
            style={{ fontSize: 12 }}
          >
            Page 1 · Front
          </div>
          <div
            className="shadow-[0_30px_80px_rgba(0,0,0,0.6)] border border-white/10 overflow-hidden relative"
            style={{ width: SHEET_W * 0.55, height: SHEET_H * 0.55 }}
          >
            <div
              ref={frontRef}
              data-testid="tableflyer-front-sheet"
              style={{
                transform: "scale(0.55)",
                transformOrigin: "top left",
                width: SHEET_W,
                height: SHEET_H,
              }}
            >
              <FrontPage />
            </div>
          </div>
        </div>

        {/* BACK */}
        <div className="flex flex-col items-center gap-4">
          <div
            className="font-['Anton'] uppercase tracking-[0.4em] text-zinc-400"
            style={{ fontSize: 12 }}
          >
            Page 2 · Back · Full Menu
          </div>
          <div
            className="shadow-[0_30px_80px_rgba(0,0,0,0.6)] border border-white/10 overflow-hidden relative"
            style={{ width: SHEET_W * 0.55, height: SHEET_H * 0.55 }}
          >
            <div
              ref={backRef}
              data-testid="tableflyer-back-sheet"
              style={{
                transform: "scale(0.55)",
                transformOrigin: "top left",
                width: SHEET_W,
                height: SHEET_H,
              }}
            >
              <BackPage />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom action bar — downloads + print together */}
      <div className="mt-12 flex flex-col items-center gap-4">
        <div
          className="font-['Anton'] uppercase tracking-[0.4em] text-zinc-400"
          style={{ fontSize: 12 }}
        >
          Downloads
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
          <button
            onClick={() => download("front")}
            disabled={downloading !== null}
            data-testid="tableflyer-download-front-btn"
            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-white hover:text-black disabled:opacity-60 disabled:cursor-not-allowed text-white font-['Anton'] tracking-widest uppercase px-6 py-3 text-xs transition-colors shadow-[0_8px_24px_rgba(255,59,48,0.4)]"
          >
            {downloading === "front" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Building…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download Front PNG
              </>
            )}
          </button>
          <button
            onClick={() => download("back")}
            disabled={downloading !== null}
            data-testid="tableflyer-download-back-btn"
            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-white hover:text-black disabled:opacity-60 disabled:cursor-not-allowed text-white font-['Anton'] tracking-widest uppercase px-6 py-3 text-xs transition-colors shadow-[0_8px_24px_rgba(255,59,48,0.4)]"
          >
            {downloading === "back" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Building…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download Back PNG
              </>
            )}
          </button>
          <button
            onClick={() => window.print()}
            data-testid="tableflyer-print-btn"
            className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-white/20 text-white font-['Anton'] tracking-widest uppercase px-6 py-3 text-xs transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print both pages
          </button>
        </div>
        <p className="font-['Outfit'] text-zinc-400 text-xs max-w-xl text-center">
          Each PNG exports at 1632×2112 (2× resolution). Print on Letter (8.5″ × 11″) — front
          on one side, back on the other for a clean double-sided table card.
        </p>
      </div>

      {/* PRINT-ONLY full-resolution copies (each on its own page) */}
      <div className="print-only">
        <div className="print-page">
          <FrontPage />
        </div>
        <div className="print-page">
          <BackPage />
        </div>
      </div>

      <style>{`
        .print-only { display: none; }
        @media print {
          @page { size: letter; margin: 0; }
          html, body {
            background: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .tableflyer-root > *:not(.print-only) { display: none !important; }
          .print-only { display: block; }
          .print-page {
            width: 8.5in;
            height: 11in;
            page-break-after: always;
            overflow: hidden;
          }
          .print-page:last-child { page-break-after: auto; }
        }
      `}</style>
    </div>
  );
}
